import { renderIcon } from "Components/wec-e-icon/wec-e-icon.template";
import { renderCssClasses } from "Helper/renderHelper/renderCssClasses";
import { html } from "lit-html";
import { ifDefined } from "lit-html/directives/if-defined";

import { WECLazyImage } from "./wec-e-lazy-image";
import lazyImageShadowScss from "./wec-e-lazy-image.shadow.scss";
import { RenderLazyImageArgs } from "./wec-e-lazy-image.types";

export interface LazyImageTemplateArgs {
  src: string;
  srcset?: string;
  sizes?: string;
  alt?: string;
  aspectRatio?: string;
  component: WECLazyImage;
}

const shouldRenderErrorPlaceholder = (args: LazyImageTemplateArgs) => {
  return !(args.component.hasError === false || args.component.loadingPlaceholderLoaded === true);
};

const renderErrorPlaceholder = (args: LazyImageTemplateArgs, forceRender = false) => {
  if (forceRender === false && !shouldRenderErrorPlaceholder(args)) return "";

  return html`
    <div class="wec-e-lazy-image__error-placeholder-box">
      ${renderIcon({
        iconId: "wec-icon-placeholder",
        cssClasses: "wec-e-lazy-image__error-placeholder",
      })}
    </div>
  `;
};

const renderPlaceholder = (args: LazyImageTemplateArgs) => {
  if (args.component.loadingPlaceholder === "" || args.component.loadingPlaceholderLoadingError === true) {
    return renderErrorPlaceholder(args, true);
  }

  return html`
    <img
      draggable="false"
      class="wec-e-lazy-image__placeholder"
      alt="${ifDefined(args.alt)}"
      src="${args.component.loadingPlaceholder}"
      @load=${() => {
        args.component.loadingPlaceholderLoaded = true;
        args.component.loadingPlaceholderLoadingError = false;
      }}
      @error=${() => {
        args.component.loadingPlaceholderLoaded = false;
        args.component.loadingPlaceholderLoadingError = true;
      }}
    />
  `;
};

const renderImage = (args: LazyImageTemplateArgs) => {
  return html`
    <img
      draggable="false"
      class="wec-e-lazy-image__image"
      src="${args.src}"
      srcset="${ifDefined(args.srcset)}"
      alt="${ifDefined(args.alt)}"
      sizes="${ifDefined(args.sizes)}"
    />
  `;
};

export const lazyImageTemplate = (args: LazyImageTemplateArgs) => {
  return html`
    <style>
      ${lazyImageShadowScss}
    </style>
    <div
      class="wec-e-lazy-image__image-box"
      data-aspect-ratio="${ifDefined(args.aspectRatio)}"
      ?data-image-loaded="${args.component.imageLoaded}"
      ?data-loading-error="${args.component.hasError}"
    >
      ${renderPlaceholder(args)} ${renderImage(args)}
    </div>
  `;
};

export const renderLazyImage = (args: RenderLazyImageArgs) => {
  return html`
    <wec-e-lazy-image
      class="wec-e-lazy-image${renderCssClasses(args.cssClasses)}"
      src="${args.src}"
      alt=${ifDefined(args.alt)}
      srcset=${ifDefined(args.srcset)}
      sizes=${ifDefined(args.sizes)}
      loading-placeholder=${ifDefined(args.loadingPlaceholder)}
      aspect-ratio=${ifDefined(args.aspectRatio)}
    >
    </wec-e-lazy-image>
  `;
};

export interface RenderLazyImagePlaceholderArgs {
  cssClasses?: string;
  aspectRatio: string;
}

export const renderLazyImagePlaceholder = (args: RenderLazyImagePlaceholderArgs) => {
  return html`
    <div
      class="wec-e-lazy-image wec-e-lazy-image--placeholder${renderCssClasses(args.cssClasses)}"
      aspect-ratio="${args.aspectRatio}"
    >
      <div class="wec-e-lazy-image__placeholder-content">
        ${renderIcon({
          cssClasses: "wec-e-lazy-image__placeholder-icon",
          iconId: "wec-icon-placeholder",
        })}
      </div>
    </div>
  `;
};
